import { observer } from 'mobx-react-lite'
import __users, { PUSH_SEGMENT_PLAN, UsersSegment } from './users.controller'
import { useEffect } from "react";
import { reaction } from 'mobx'
import i from '../../translations/i'
import { Titles } from '../../layouts/Titles'
import { Button, Input, Segmented, Select, Tooltip } from "antd";
import {
    EditOutlined,
    FilterOutlined,
    GiftFilled,
    IdcardOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SearchOutlined,
    SendOutlined,
    StopOutlined,
    TeamOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { moment_custom } from '../../helpers/timestamp/datetime.helper'
import projectTime from '../../helpers/project_time'
import { storeLocal } from '../../helpers/stores'
import { ErrorByPricingPlan } from '../../components/ErrorByPricingPlan'
import { WarehousesController } from '../settings/warehouses/warehouses.controller'
import price_types_controller from '../settings/price_types/controller'
import {
    CardList,
    cleanText,
    ListCard,
    ListChip,
    ListRange,
    ListToolbar,
    ListToolbarBreak,
    ListToolbarChips,
    ListToolbarField,
    ListToolbarSpacer,
} from '../../components/card_list/CardList'

dayjs.extend(utc)

const warehouses = new WarehousesController()

// Типы цен в фильтре — под внутренним названием из настроек, как их знают сотрудники
const priceTypeOptions = (): { id: number, label: string }[] => price_types_controller.items
    .filter((el: any) => el.id)
    .map((el: any) => ({ id: el.id, label: el.private_name || el.name || `#${el.id}` }))

// Даты с бэкенда: ISO у created_at и last_order_date, «Y-m-d H:i:s» в UTC у last_activity
const formatDate = (value: string | null, format = 'D MMM YYYY') => value ? moment_custom(dayjs.utc(value).valueOf(), format) : null

// Российские номера показываем как +7 999 123-45-67, остальные — как есть
const formatPhone = (phone: string | null) => {
    const raw = cleanText(phone)
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8'))
        return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    return raw
}

// Быстрые сегменты задают только границы по заказам и дням; выбранные товары, склады и типы цен не трогают
type SegmentRanges = Omit<UsersSegment, 'products' | 'warehouses' | 'price_types'>

const QUICK_SEGMENTS: { key: string, segment: SegmentRanges }[] = [
    { key: 'noOrders', segment: { orders_from: 0, orders_to: 0, days_from: null, days_to: null } },
    { key: 'oneOrder', segment: { orders_from: 1, orders_to: 1, days_from: null, days_to: null } },
    { key: 'loyal', segment: { orders_from: 3, orders_to: null, days_from: null, days_to: null } },
    { key: 'sleeping', segment: { orders_from: null, orders_to: null, days_from: 30, days_to: null } },
]

const NO_RANGES: SegmentRanges = { orders_from: null, orders_to: null, days_from: null, days_to: null }

const isQuickActive = (segment: SegmentRanges) =>
    (Object.keys(segment) as (keyof typeof segment)[]).every(key => (__users.list_filter[key] ?? null) === segment[key])

// День рождения — по дате проекта, как в карточке клиента и в рассылке пушей к дню рождения
const isBirthdayToday = (birthday: number | null) => {
    if (!birthday || birthday <= 0) return false
    return projectTime.wall(birthday).format('MM-DD') === projectTime.wall(dayjs().unix()).format('MM-DD')
}

const UserCard = observer(({ user }: { user: any }) => {
    const name = [user.firstname, user.lastname, user.surname].map(cleanText).filter(Boolean).join(' ')
    const email = cleanText(user.email)
    const phone = formatPhone(user.phone)
    const externalId = cleanText(user.external_id)
    const birthdayToday = isBirthdayToday(user.birthday)

    return (
        <ListCard
            href={`/users/${user.id}`}
            muted={!!user.deleted_at}
            title={name || i.t('users:cards.noName')}
            titleHint={name}
            titleExtra={<>
                {user.deleted_at &&
                    <ListChip tone="danger">{i.t('users:cards.deletedAt', { date: formatDate(user.deleted_at) })}</ListChip>
                }
                {!user.deleted_at && !!user.is_blocked &&
                    <ListChip tone="warning" icon={<StopOutlined />}>{i.t('users:profile.blocked')}</ListChip>
                }
            </>}
            subtitle={<>
                ID {user.id}
                {user.created_at && <> · {i.t('users:profile.clientSince', { date: formatDate(user.created_at) })}</>}
                {user.last_activity && <> · {i.t('users:cards.lastSeen', { date: formatDate(user.last_activity) })}</>}
            </>}
            chips={(email || phone || externalId || user.birthday > 0) && <>
                {phone && <ListChip icon={<PhoneOutlined />} copy={phone}>{phone}</ListChip>}
                {email && <ListChip icon={<MailOutlined />} copy={email}>{email}</ListChip>}
                {user.birthday > 0 &&
                    <ListChip
                        icon={<GiftFilled />}
                        tone={birthdayToday ? 'primary' : undefined}
                        tooltip={birthdayToday ? i.t('users:profile.birthdayToday') : i.t('users:birthday')}
                    >
                        {moment_custom(projectTime.wallMs(user.birthday), 'D MMMM YYYY')}
                    </ListChip>
                }
                {externalId &&
                    <ListChip icon={<IdcardOutlined />} copy={externalId} tooltip={i.t('users:cards.externalId')}>{externalId}</ListChip>
                }
            </>}
            metricWidth={120}
            // «Заказов» — по тем же правилам, что и фильтры сегмента: все, кроме отменённых
            metrics={[
                {
                    value: user.orders_count !== undefined ? user.orders_count.toLocaleString() : null,
                    label: i.t('users:cards.orders'),
                    hint: i.t('users:cards.ordersHint'),
                    tone: user.orders_count > 0 ? 'success' : undefined,
                },
                {
                    value: (user.count_payed_orders ?? 0).toLocaleString(),
                    label: i.t('users:cards.paidOrders'),
                    hint: i.t('users:cards.paidOrdersHint'),
                },
                { value: formatDate(user.last_order_at ?? user.last_order_date), label: i.t('users:cards.lastOrder') },
                {
                    value: user.days_without_order === null || user.days_without_order === undefined
                        ? null
                        : user.days_without_order === 0 ? i.t('users:cards.today') : i.t('users:cards.days', { count: user.days_without_order }),
                    label: i.t('users:cards.daysWithout'),
                },
            ]}
            actions={
                <Tooltip title={i.t('def:actions.edit')}>
                    <Button shape="circle" icon={<EditOutlined />} href={`/users/${user.id}`} />
                </Tooltip>
            }
        />
    )
})

export const Users = observer(() => {

    useEffect(() => {
        projectTime.load()
        __users.readFiltersFromUrl()
        __users.getList()

        warehouses.pagination.limit = 9999999
        warehouses.get()
        price_types_controller.get()

        // Фильтры — в адресе страницы, чтобы ссылкой можно было поделиться и вернуться к сегменту из рассылки
        const disposeUrl = reaction(
            () => __users.filtersUrl(),
            url => window.history.replaceState(null, '', url),
        )
        // Названия складов и типов цен нужны редактору пуша, чтобы показать условия сегмента
        const disposeWarehouses = reaction(
            () => warehouses.list,
            list => __users.setWarehouseLabels(list ?? []),
        )
        const disposePriceTypes = reaction(
            () => priceTypeOptions(),
            list => __users.setPriceTypeLabels(list),
            { fireImmediately: true },
        )

        return () => {
            disposeUrl()
            disposeWarehouses()
            disposePriceTypes()
        }
    }, [])

    const buttons = [
        { title: i.t('def:actions.create'), type: 'dashed', href: '/users/0' },
        { title: i.t('users:excelExport'), type: 'primary', click: () => __users.exportUsers('xlsx') },
        { title: i.t('users:csvExport'), type: 'primary', click: () => __users.exportUsers('csv') }
    ]

    const { list, list_filter } = __users
    const searched = !!(list_filter.name || list_filter.phone || list_filter.email) || __users.segmentActive
    const filtered = searched || (list_filter.status ?? 'active') !== 'active'
    // Рассылка — по найденным действующим клиентам; удалённым пуши не отправляем
    const canPush = searched && list_filter.status !== 'deleted' && list.count > 0

    return(
        <>
            <Titles
                title={i.t('users:listUsers')}
                buttons={buttons}
                breadcrumb={[
                    {title: i.t('users:listUsers')}
                ]}
            />

            <CardList
                items={__users.users}
                loading={__users.listLoading}
                count={list.count}
                extra={canPush &&
                    <ErrorByPricingPlan
                        plan={PUSH_SEGMENT_PLAN}
                        content={
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                href={__users.pushUrl}
                                disabled={storeLocal.get('pricing_plan') < PUSH_SEGMENT_PLAN}
                            >
                                {i.t('users:segment.sendPush')}
                            </Button>
                        }
                    />
                }
                toolbar={
                    <ListToolbar>
                        <ListToolbarField>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder={i.t('users:cards.searchName')}
                                value={list_filter.name ?? ''}
                                onChange={e => __users.setFilter('name', e.target.value)}
                            />
                        </ListToolbarField>
                        <ListToolbarField>
                            <Input
                                allowClear
                                prefix={<PhoneOutlined />}
                                placeholder={i.t('users:phone')}
                                value={list_filter.phone ?? ''}
                                onChange={e => __users.setFilter('phone', e.target.value)}
                            />
                        </ListToolbarField>
                        <ListToolbarField>
                            <Input
                                allowClear
                                prefix={<MailOutlined />}
                                placeholder={i.t('users:email')}
                                value={list_filter.email ?? ''}
                                onChange={e => __users.setFilter('email', e.target.value)}
                            />
                        </ListToolbarField>
                        <ListToolbarSpacer />
                        <Segmented
                            value={list_filter.status ?? 'active'}
                            options={[
                                { label: i.t('users:statusActive'), value: 'active' },
                                { label: i.t('users:statusDeleted'), value: 'deleted' },
                                { label: i.t('users:statusAll'), value: 'all' }
                            ]}
                            onChange={value => __users.setStatusFilter(`${value}`)}
                        />

                        <ListToolbarBreak
                            title={<><FilterOutlined /> {i.t('users:segment.title')}</>}
                            open={__users.segmentOpen}
                            onToggle={() => __users.toggleSegment()}
                            count={__users.segmentCount}
                            extra={__users.segmentActive &&
                                <Button size="small" type="link" onClick={() => __users.resetSegment()}>{i.t('users:segment.reset')}</Button>
                            }
                        />
                        {__users.segmentOpen && <>
                            <ListToolbarField label={i.t('users:segment.orders')} hint={i.t('users:segment.ordersHint')}>
                                <ListRange
                                    from={list_filter.orders_from}
                                    to={list_filter.orders_to}
                                    onChange={(from, to) => {
                                        __users.setFilter('orders_from', from)
                                        __users.setFilter('orders_to', to)
                                    }}
                                />
                            </ListToolbarField>
                            <ListToolbarField label={i.t('users:segment.days')} hint={i.t('users:segment.daysHint')}>
                                <ListRange
                                    from={list_filter.days_from}
                                    to={list_filter.days_to}
                                    onChange={(from, to) => {
                                        __users.setFilter('days_from', from)
                                        __users.setFilter('days_to', to)
                                    }}
                                />
                            </ListToolbarField>
                            <ListToolbarField wide label={i.t('users:segment.products')} hint={i.t('users:segment.productsHint')}>
                                <Select
                                    mode="multiple"
                                    labelInValue
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    maxTagCount="responsive"
                                    style={{ width: '100%' }}
                                    placeholder={i.t('users:segment.productsPlaceholder')}
                                    value={list_filter.products.map((id: number) => ({ value: id, label: __users.productLabels[id] ?? `#${id}` }))}
                                    options={__users.productOptions}
                                    onSearch={value => __users.searchProducts(value)}
                                    onChange={(options: any) => __users.setProducts(options ?? [])}
                                    notFoundContent={null}
                                />
                            </ListToolbarField>
                            {warehouses.list.length > 0 &&
                                <ListToolbarField label={i.t('users:segment.warehouses')} hint={i.t('users:segment.warehousesHint')}>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder={i.t('users:segment.warehousesPlaceholder')}
                                        options={warehouses.list.map((el: any) => ({ label: el.name, value: el.id }))}
                                        value={list_filter.warehouses}
                                        onChange={(ids: number[]) => __users.setWarehouses(ids ?? [])}
                                    />
                                </ListToolbarField>
                            }
                            {priceTypeOptions().length > 0 &&
                                <ListToolbarField label={i.t('users:segment.priceTypes')} hint={i.t('users:segment.priceTypesHint')}>
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        maxTagCount="responsive"
                                        style={{ width: '100%' }}
                                        placeholder={i.t('users:segment.priceTypesPlaceholder')}
                                        options={priceTypeOptions().map(el => ({ label: el.label, value: el.id }))}
                                        value={list_filter.price_types}
                                        onChange={(ids: number[]) => __users.setPriceTypes(ids ?? [])}
                                    />
                                </ListToolbarField>
                            }
                            <ListToolbarChips label={i.t('users:segment.quick')}>
                                {QUICK_SEGMENTS.map(({ key, segment }) => {
                                    const active = isQuickActive(segment)
                                    return (
                                        <ListChip key={key} active={active} onClick={() => __users.setSegment(active ? NO_RANGES : segment)}>
                                            {i.t(`users:segment.${key}`)}
                                        </ListChip>
                                    )
                                })}
                            </ListToolbarChips>
                        </>}
                    </ListToolbar>
                }
                renderItem={(user: any) => <UserCard user={user} />}
                empty={filtered
                    ? { icon: <SearchOutlined />, title: i.t('users:cards.empty'), text: i.t('users:cards.emptyHint') }
                    : {
                        icon: <TeamOutlined />,
                        title: i.t('users:cards.emptyAll'),
                        text: i.t('users:cards.emptyAllHint'),
                        action: <Button type="primary" icon={<PlusOutlined />} href="/users/0">{i.t('def:actions.create')}</Button>,
                    }
                }
                pagination={{
                    current: Math.floor(list.offset / list.limit) + 1,
                    pageSize: list.limit,
                    total: list.count,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        if (pageSize !== list.limit) {
                            list.limit = pageSize
                            list.offset = 0
                        } else {
                            list.offset = (page - 1) * list.limit
                        }
                        __users.getList()
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                }}
            />
        </>
    )
})
